<template>
  <section class="reviews-section" :class="`reviews-section--${mode}`">
    <!-- SPLIT MODE (home + pricing): Wall of Love -->
    <template v-if="mode === 'split'">
      <header class="wall-header">
        <span v-if="showEyebrow" class="wall-eyebrow">
          <span class="wall-eyebrow-dot"></span>
          Real reviews
        </span>
        <h2 class="wall-heading">{{ heading }}</h2>
        <p v-if="intro" class="wall-intro">{{ intro }}</p>

        <!-- Location filter: pill-style segmented control. Lets visitors
             narrow the merged review wall down to a single location. -->
        <div
          v-if="showFilter"
          class="wall-filter"
          role="tablist"
          aria-label="Filter reviews by location"
        >
          <button
            v-for="opt in filterOptions"
            :key="opt.key"
            type="button"
            role="tab"
            class="wall-filter-btn"
            :class="{ 'wall-filter-btn--active': activeFilter === opt.key }"
            :aria-selected="activeFilter === opt.key"
            @click="activeFilter = opt.key"
          >{{ opt.label }}</button>
        </div>
      </header>

      <div v-if="showStats" class="stats-bar">
        <article
          v-for="loc in stats"
          :key="loc.key"
          class="stats-card"
          :class="`stats-card--${loc.key}`"
        >
          <router-link
            :to="`/${loc.key}`"
            class="stats-card-location"
            :aria-label="`Visit the GYMPODS ${loc.name} page`"
          >
            <span class="loc-dot" aria-hidden="true"></span>
            <!-- One line, so the space stays and the link text reads "GYMPODS Dalston". -->
            <span class="loc-eyebrow">GYMPODS</span> <span class="loc-city">{{ loc.name }}</span>
          </router-link>
          <ul class="stats-card-platforms">
            <li
              v-for="(p, idx) in loc.platforms"
              :key="p.name"
              class="stat-col-wrap"
              :class="{ 'stat-col-wrap--divided': idx > 0 }"
            >
              <a
                :href="p.url"
                target="_blank"
                rel="noopener noreferrer"
                class="stat-col"
                :aria-label="`View GYMPODS ${loc.name} reviews on ${p.name} (opens in new tab)`"
              >
                <span class="stat-col-platform">
                  <IconGoogle :size="18" class="stat-col-brand" v-if="p.isGoogle"
                     
                    aria-hidden="true" />
                  <span class="stat-col-name">{{ p.name }}</span>
                </span>
                <span class="stat-col-rating">
                  <Star :size="14" class="stat-col-star" aria-hidden="true" />
                  <strong>{{ p.rating }}</strong>
                </span>
                <span class="stat-col-count">{{ p.countLabel }}</span>
              </a>
            </li>
          </ul>
        </article>
      </div>

      <div v-if="showCards" class="wall-body">
        <div class="wall-carousel">
          <button
            v-if="splitPageCount > 1"
            type="button"
            class="carousel-arrow carousel-arrow--prev"
            :disabled="splitPage === 0"
            aria-label="Previous reviews"
            @click="prevSplit"
          >
            <ChevronLeft :size="16" aria-hidden="true" />
          </button>

          <ul class="wall-grid">
            <li
              v-for="r in splitPagedItems"
              :key="`s-${r.id}`"
              class="wall-card"
              :class="[
                `wall-card--${r.location}`,
                { 'wall-card--expanded': !!expanded[r.id] },
              ]"
            >
              <div class="wall-card-head">
                <div
                  class="wall-card-stars"
                  role="img"
                  :aria-label="`${r.rating} out of 5 stars`"
                >
                  <Star :size="14" v-for="n in 5"
                    :key="n"
                     
                    :class="{ filled: n <= r.rating }"
                    aria-hidden="true" />
                </div>
                <span class="wall-card-tag">
                  <span class="wall-card-tag-dot" aria-hidden="true"></span>
                  {{ r.locationName }}
                </span>
              </div>
              <p
                class="wall-card-text"
                :class="{ 'wall-card-text--clamped': isClamped(r) }"
              >{{ r.text }}</p>
              <button
                v-if="isLong(r)"
                type="button"
                class="wall-more"
                :aria-expanded="!!expanded[r.id]"
                @click="toggle(r.id)"
              >{{ expanded[r.id] ? 'Show less' : 'Read more' }}</button>
              <footer class="wall-card-foot">
                <span class="wall-card-avatar" aria-hidden="true">{{
                  r.author.charAt(0)
                }}</span>
                <div class="wall-card-meta">
                  <span class="wall-card-author">{{ r.author }}</span>
                  <time class="wall-card-date" :datetime="r.date">{{ formatDate(r.date) }}</time>
                </div>
                <a
                  :href="googleUrlFor(r.location)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="wall-card-google"
                  :aria-label="`View GYMPODS ${r.locationName} reviews on Google Maps (opens in new tab)`"
                >
                  <IconGoogle :size="18" aria-hidden="true" />
                </a>
              </footer>
            </li>
          </ul>

          <button
            v-if="splitPageCount > 1"
            type="button"
            class="carousel-arrow carousel-arrow--next"
            :disabled="splitPage >= splitPageCount - 1"
            aria-label="Next reviews"
            @click="nextSplit"
          >
            <ChevronRight :size="16" aria-hidden="true" />
          </button>
        </div>

        <div v-if="splitPageCount > 1" class="carousel-dots" role="group" aria-label="Reviews pagination">
          <button
            v-for="i in splitPageCount"
            :key="i"
            type="button"
            class="carousel-dot"
            :class="{ 'carousel-dot--active': splitPage === i - 1 }"
            :aria-label="`Go to page ${i}`"
            :aria-current="splitPage === i - 1 ? 'true' : 'false'"
            @click="goToSplit(i - 1)"
          ></button>
        </div>
      </div>
    </template>

    <!-- SINGLE MODE (/dalston, /putney): unchanged -->
    <template v-else>
      <header class="reviews-header">
        <h2>{{ heading }}</h2>
        <p v-if="intro" class="reviews-intro">{{ intro }}</p>

        <div v-if="summary" class="reviews-summary">
          <div
            class="summary-stars"
            :aria-label="`${formatRating(summary.averageRating)} out of 5 stars`"
          >
            <Star :size="14" v-for="n in 5"
              :key="n"
               
              :class="{ filled: n <= Math.round(summary.averageRating) }"
              aria-hidden="true" />
            <span class="summary-score">{{ formatRating(summary.averageRating) }}</span>
          </div>
          <a
            class="summary-meta"
            :href="summary.placeUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            Based on {{ summary.googleCountLabel || summary.reviewCount }} Google reviews
            <ExternalLink :size="14" aria-hidden="true" />
          </a>
        </div>

        <div v-if="platforms.length" class="platforms-strip">
          <span class="platforms-label">Also rated on</span>
          <ul class="platforms-list">
            <li v-for="p in platforms" :key="p.name">
              <component
                :is="p.url ? 'a' : 'span'"
                :href="p.url || null"
                :target="p.url ? '_blank' : null"
                :rel="p.url ? 'noopener noreferrer' : null"
                :aria-label="p.url ? `View GYMPODS reviews on ${p.name} (opens in new tab)` : null"
                class="platform-chip"
                :class="{ 'platform-chip--link': !!p.url }"
              >
                <span class="platform-name">{{ p.name }}</span>
                <span class="platform-score">
                  <Star :size="14" aria-hidden="true" />
                  {{ p.rating }}
                </span>
                <span class="platform-count">{{ p.countLabel }}</span>
                <ExternalLink :size="14" class="platform-ext" v-if="p.url"
                   
                  aria-hidden="true" />
              </component>
            </li>
          </ul>
        </div>
      </header>

      <div class="reviews-body reviews-body--single">
        <div class="reviews-carousel">
          <button
            v-if="singlePageCount > 1"
            type="button"
            class="carousel-arrow carousel-arrow--prev"
            :disabled="singlePage === 0"
            aria-label="Previous reviews"
            @click="prevSingle"
          >
            <ChevronLeft :size="16" aria-hidden="true" />
          </button>

          <ul class="reviews-grid">
            <li
              v-for="r in singlePagedItems"
              :key="r.id"
              class="review-card"
              :class="{ 'review-card--expanded': !!expanded[r.id] }"
            >
              <div
                class="review-stars"
                role="img"
                :aria-label="`${r.rating} out of 5 stars`"
              >
                <Star :size="14" v-for="n in 5"
                  :key="n"
                   
                  :class="{ filled: n <= r.rating }"
                  aria-hidden="true" />
              </div>
              <blockquote
                class="review-text"
                :class="{ 'review-text--clamped': isClamped(r) }"
              >{{ r.text }}</blockquote>
              <button
                v-if="isLong(r)"
                type="button"
                class="review-more"
                :aria-expanded="!!expanded[r.id]"
                @click="toggle(r.id)"
              >{{ expanded[r.id] ? 'Show less' : 'Read more' }}</button>
              <footer class="review-meta">
                <span class="review-avatar" aria-hidden="true">{{
                  r.author.charAt(0)
                }}</span>
                <div class="review-meta-text">
                  <span class="review-author">{{ r.author }}</span>
                  <time class="review-date" :datetime="r.date">{{ formatDate(r.date) }}</time>
                </div>
                <a
                  :href="googleUrlFor(location)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="review-google"
                  :aria-label="`View GYMPODS reviews on Google Maps (opens in new tab)`"
                >
                  <IconGoogle :size="18" aria-hidden="true" />
                </a>
              </footer>
            </li>
          </ul>

          <button
            v-if="singlePageCount > 1"
            type="button"
            class="carousel-arrow carousel-arrow--next"
            :disabled="singlePage >= singlePageCount - 1"
            aria-label="Next reviews"
            @click="nextSingle"
          >
            <ChevronRight :size="16" aria-hidden="true" />
          </button>
        </div>

        <div v-if="singlePageCount > 1" class="carousel-dots" role="group" aria-label="Reviews pagination">
          <button
            v-for="i in singlePageCount"
            :key="i"
            type="button"
            class="carousel-dot"
            :class="{ 'carousel-dot--active': singlePage === i - 1 }"
            :aria-label="`Go to page ${i}`"
            :aria-current="singlePage === i - 1 ? 'true' : 'false'"
            @click="goToSingle(i - 1)"
          ></button>
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import { ChevronLeft, ChevronRight, ExternalLink, Star } from 'lucide-vue-next';
import IconGoogle from '@/components/icons/IconGoogle.vue';
import { REVIEWS } from '@/data/reviews.js';
import { formatRating } from '@/utils/formatRating';

const LOCATION_NAMES = { dalston: 'Dalston', putney: 'Putney' };

export default {
  name: 'ReviewsSection',
  components: { ChevronLeft, ChevronRight, ExternalLink, Star, IconGoogle },
  props: {
    location: {
      type: String,
      required: true,
      validator: (v) => ['dalston', 'putney', 'all'].includes(v),
    },
    heading: {
      type: String,
      default: 'Reviews',
    },
    intro: {
      type: String,
      default: '',
    },
    showStats: {
      type: Boolean,
      default: true,
    },
    showCards: {
      type: Boolean,
      default: true,
    },
    showEyebrow: {
      type: Boolean,
      default: true,
    },
    showFilter: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      expanded: {},
      clampCharThreshold: 220,
      singlePage: 0,
      splitPage: 0,
      // Active location filter for split mode (home page reviews).
      // 'all' shows merged Dalston + Putney; 'dalston' / 'putney' filter.
      activeFilter: 'all',
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
    };
  },
  mounted() {
    // rAF-throttled resize. The reactive write changes splitPageSize /
    // singlePageSize, which forces a re-render of the carousel pages.
    // During an active resize-drag this fires 60+ times per second — we
    // only need the final settle. Coalescing to one update per frame
    // cuts the input-handler cost without changing the user-visible
    // result.
    this._onResize = () => {
      if (this._resizePending) return;
      this._resizePending = true;
      window.requestAnimationFrame(() => {
        this._resizePending = false;
        const next = window.innerWidth;
        if (next !== this.viewportWidth) this.viewportWidth = next;
      });
    };
    window.addEventListener('resize', this._onResize, { passive: true });
  },
  beforeUnmount() {
    if (this._onResize) window.removeEventListener('resize', this._onResize);
  },
  watch: {
    splitPageCount(n) {
      if (this.splitPage > n - 1) this.splitPage = Math.max(0, n - 1);
    },
    singlePageCount(n) {
      if (this.singlePage > n - 1) this.singlePage = Math.max(0, n - 1);
    },
    activeFilter() {
      // Reset to the first page whenever the user changes the filter, so
      // they always see the newest reviews for the selected location.
      this.splitPage = 0;
    },
  },
  computed: {
    mode() {
      return this.location === 'all' ? 'split' : 'single';
    },
    stats() {
      if (this.mode !== 'split') return [];
      return ['dalston', 'putney'].map((key) => {
        const loc = REVIEWS[key];
        return {
          key,
          name: LOCATION_NAMES[key],
          platforms: [
            {
              name: 'Google',
              rating: formatRating(loc.averageRating),
              countLabel: `${loc.googleCountLabel || loc.reviewCount} reviews`,
              url: loc.placeUrl,
              isGoogle: true,
            },
            ...(loc.platforms || []).map((p) => ({ ...p, rating: formatRating(p.rating), isGoogle: false })),
          ],
        };
      });
    },
    mergedItems() {
      if (this.mode !== 'split') return [];
      const keys = this.activeFilter === 'all'
        ? ['dalston', 'putney']
        : [this.activeFilter];
      const items = [];
      for (const key of keys) {
        for (const r of REVIEWS[key].items) {
          items.push({ ...r, location: key, locationName: LOCATION_NAMES[key] });
        }
      }
      items.sort((a, b) => new Date(b.date) - new Date(a.date));
      return items;
    },
    filterOptions() {
      if (this.mode !== 'split') return [];
      return [
        { key: 'all', label: 'All' },
        { key: 'dalston', label: LOCATION_NAMES.dalston },
        { key: 'putney', label: LOCATION_NAMES.putney },
      ];
    },
    splitPageSize() {
      // Match the grid column count so pages always fill a single clean row.
      if (this.viewportWidth <= 768) return 1;
      if (this.viewportWidth <= 999) return 2;
      return 3;
    },
    splitPageCount() {
      return Math.max(
        1,
        Math.ceil(this.mergedItems.length / this.splitPageSize),
      );
    },
    splitPagedItems() {
      const start = this.splitPage * this.splitPageSize;
      return this.mergedItems.slice(start, start + this.splitPageSize);
    },
    singleItems() {
      if (this.mode !== 'single') return [];
      // Newest first so location pages match the chronological order
      // already used on the home page via combinedReviews().
      return [...REVIEWS[this.location].items].sort(
        (a, b) => new Date(b.date) - new Date(a.date),
      );
    },
    singlePageSize() {
      // Match the grid column count so pages always fill a single clean row.
      if (this.viewportWidth <= 768) return 1;
      if (this.viewportWidth <= 999) return 2;
      return 3;
    },
    singlePageCount() {
      return Math.max(
        1,
        Math.ceil(this.singleItems.length / this.singlePageSize),
      );
    },
    singlePagedItems() {
      const start = this.singlePage * this.singlePageSize;
      return this.singleItems.slice(start, start + this.singlePageSize);
    },
    summary() {
      if (this.mode !== 'single') return null;
      const loc = REVIEWS[this.location];
      return {
        averageRating: loc.averageRating,
        reviewCount: loc.reviewCount,
        googleCountLabel: loc.googleCountLabel,
        placeUrl: loc.placeUrl,
      };
    },
    platforms() {
      if (this.mode !== 'single') return [];
      return (REVIEWS[this.location].platforms || []).map((p) => ({ ...p, rating: formatRating(p.rating) }));
    },
  },
  methods: {
    formatRating,
    prevSingle() {
      if (this.singlePage > 0) this.singlePage -= 1;
    },
    nextSingle() {
      if (this.singlePage < this.singlePageCount - 1) this.singlePage += 1;
    },
    goToSingle(i) {
      this.singlePage = Math.max(0, Math.min(i, this.singlePageCount - 1));
    },
    prevSplit() {
      if (this.splitPage > 0) this.splitPage -= 1;
    },
    nextSplit() {
      if (this.splitPage < this.splitPageCount - 1) this.splitPage += 1;
    },
    goToSplit(i) {
      this.splitPage = Math.max(0, Math.min(i, this.splitPageCount - 1));
    },
    googleUrlFor(locationKey) {
      return REVIEWS[locationKey]?.placeUrl;
    },
    isLong(r) {
      return r.text.length > this.clampCharThreshold;
    },
    isClamped(r) {
      return this.isLong(r) && !this.expanded[r.id];
    },
    toggle(id) {
      this.expanded = { ...this.expanded, [id]: !this.expanded[id] };
    },
    // Absolute "Month Year" output so prerendered HTML never goes stale.
    // The ISO `r.date` lives in the parent <time datetime> attribute,
    // giving crawlers / a11y tools the precise day while users see a
    // stable, human-readable month.
    formatDate(iso) {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    },
  },
};
</script>

<style lang="scss" scoped>
$aqua: #7fc0c3;
$charcoal: #231F20;
$carbon: #58595B;
$zest: #D8F789;
$midnight: #193446;

.reviews-section {
  text-align: left;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
  box-sizing: border-box;
}

/* ================================================================
   WALL OF LOVE (split mode)
================================================================ */

.wall-header {
  text-align: center;
  max-width: 820px;
  margin: 0 auto 28px;
  padding: 0 5%;
}

.wall-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: $midnight;
  background: rgba($zest, 0.45);
  padding: 7px 14px;
  border-radius: 999px;
  margin-bottom: 18px;
}

.wall-eyebrow-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: $midnight;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: -3px;
    border-radius: 50%;
    border: 2px solid rgba($midnight, 0.25);
    animation: wall-pulse 2s ease-in-out infinite;
  }
}

@keyframes wall-pulse {
  0%, 100% { transform: scale(1); opacity: 0.6; }
  50% { transform: scale(1.3); opacity: 0; }
}

.wall-heading {
  margin: 0 0 12px;
  font-size: clamp(1.8rem, 4vw, 2.8rem);
  line-height: 1.05;
  letter-spacing: -0.025em;
  color: $midnight;
  font-weight: 800;
}

.wall-intro {
  margin: 0;
  color: $carbon;
  font-size: 1.05rem;
  line-height: 1.5;
}

// Segmented pill control sitting under the wall heading. Sleek
// rounded shell with three buttons; the active option gets the
// midnight chip + white label so it reads like a single source of
// truth at a glance.
.wall-filter {
  display: inline-flex;
  margin: 18px auto 0;
  padding: 4px;
  background: rgba($midnight, 0.05);
  border: 1px solid rgba($midnight, 0.08);
  border-radius: 999px;
  gap: 2px;
}

.wall-filter-btn {
  appearance: none;
  border: none;
  background: transparent;
  padding: 8px 18px;
  border-radius: 999px;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: $carbon;
  cursor: pointer;
  transition:
    background 0.18s ease,
    color 0.18s ease,
    box-shadow 0.18s ease;

  &:hover {
    color: $midnight;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba($aqua, 0.35);
  }

  &--active {
    background: $midnight;
    color: #fff;
    box-shadow: 0 4px 14px -6px rgba($midnight, 0.45);

    &:hover { color: #fff; }
  }
}

.stats-bar {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 18px;
  margin: 0 auto 42px;
  padding: 0 5%;
  max-width: 1200px;
}

.stats-card {
  display: flex;
  align-items: stretch;
  gap: 0;
  padding: 0;
  background: #fff;
  border: 1px solid rgba($midnight, 0.08);
  border-radius: 18px;
  box-shadow: 0 10px 28px -14px rgba($midnight, 0.2);
  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
  }
}

.stats-card--dalston::before {
  background: linear-gradient(180deg, $aqua 0%, lighten($aqua, 15%) 100%);
}

.stats-card--putney::before {
  background: linear-gradient(180deg, $zest 0%, lighten($zest, 8%) 100%);
}

.stats-card-location {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 20px 26px 20px 30px;
  border-right: 1px solid rgba($midnight, 0.08);
  min-width: 150px;
  /* Was a <header>, now a <router-link> (renders as <a>) so the whole
     GYMPODS + city block is clickable through to /dalston or /putney. */
  text-decoration: none;
  color: inherit;
  transition: background 0.18s ease;

  &:hover,
  &:focus-visible {
    background: rgba($zest, 0.14);
  }

  &:focus-visible {
    outline: 2px solid $aqua;
    outline-offset: -2px;
  }
}

.stats-card--dalston .stats-card-location:hover,
.stats-card--dalston .stats-card-location:focus-visible {
  background: rgba($aqua, 0.12);
}

.loc-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-bottom: 8px;
  box-shadow: 0 0 0 3px rgba($midnight, 0.04);
}

.stats-card--dalston .loc-dot { background: $aqua; }
.stats-card--putney .loc-dot { background: darken($zest, 20%); }

.loc-eyebrow {
  font-size: 0.68rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: $carbon;
  font-weight: 700;
  margin-bottom: 2px;
}

.loc-city {
  font-size: 1.45rem;
  font-weight: 800;
  color: $midnight;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.stats-card-platforms {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: stretch;
}

.stat-col-wrap {
  display: flex;
  align-items: stretch;
}

.stat-col-wrap--divided {
  border-left: 1px solid rgba($midnight, 0.08);
}

.stat-col {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 18px 24px;
  min-width: 128px;
  text-decoration: none;
  color: inherit;
  transition: background 0.18s ease;

  &:hover,
  &:focus-visible {
    background: rgba($zest, 0.14);
  }

  &:focus-visible {
    outline: 2px solid $aqua;
    outline-offset: -2px;
  }
}

.stats-card--dalston .stat-col:hover,
.stats-card--dalston .stat-col:focus-visible {
  background: rgba($aqua, 0.12);
}

.stat-col-platform {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.7rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: $carbon;
  font-weight: 700;
}

.stat-col-brand {
  font-size: 0.85rem;
  color: $midnight;
}

.stat-col-rating {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 1.5rem;
  font-weight: 800;
  color: $midnight;
  letter-spacing: -0.02em;
  line-height: 1;

  strong { font-weight: inherit; }
}

.stat-col-star {
  color: $zest;
  font-size: 0.95rem;
  fill: currentColor;
  stroke: color-mix(in srgb, currentColor 55%, #000);
  stroke-width: 1.5;
  filter: drop-shadow(0 1px 1px rgba($midnight, 0.2));
}

.stats-card--dalston .stat-col-star { color: $aqua; }

.stat-col-count {
  font-size: 0.75rem;
  color: $carbon;
  font-weight: 500;
  white-space: nowrap;
}

/* Paginated wall carousel */

.wall-body {
  padding: 0 5%;
  max-width: 1200px;
  margin: 0 auto;
}

.wall-carousel {
  position: relative;
  display: flex;
  align-items: stretch;
  gap: 12px;
}

.wall-grid {
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-auto-rows: 1fr;
  gap: 18px;
}

/* Wall cards */

.wall-card {
  // Fixed height so paging between carousel rows never shifts the
  // viewport. The flex column + `wall-card-text` flex: 1 absorbs short
  // reviews with whitespace; long reviews stay inside the 5-line clamp.
  // (Expanded "Read more" state intentionally allows growth — that's
  // a user-triggered action, not a passive page swap.)
  height: 280px;
  background: #fff;
  border: 1px solid rgba($midnight, 0.08);
  border-radius: 16px;
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  text-align: left;
  box-shadow: 0 6px 18px -10px rgba($midnight, 0.18);
  transition: transform 0.25s ease, box-shadow 0.25s ease,
    border-color 0.25s ease, height 0.25s ease;
  position: relative;
  overflow: hidden;

  // When the user expands a clamped review, drop the fixed height so
  // the full text can flow. The grid row stretches to match (via the
  // grid-auto-rows: 1fr rule on .wall-grid).
  &--expanded {
    height: auto;
    min-height: 280px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 60%;
    height: 3px;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px -16px rgba($midnight, 0.28);
  }
}

.wall-card--dalston::before {
  background: linear-gradient(90deg, $aqua 0%, lighten($aqua, 18%) 100%);
}

.wall-card--putney::before {
  background: linear-gradient(90deg, $zest 0%, lighten($zest, 5%) 100%);
}

.wall-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

.wall-card-stars {
  display: inline-flex;
  gap: 2px;
  color: rgba($midnight, 0.22);
  font-size: 0.9rem;

  :deep(svg) {
    fill: currentColor;
    stroke: color-mix(in srgb, currentColor 55%, #000);
    stroke-width: 1.5;
  }

  .filled {
    color: $zest;
    filter: drop-shadow(0 1px 1px rgba($midnight, 0.15));
  }
}

.wall-card--dalston .wall-card-stars .filled {
  color: $aqua;
}

.wall-card-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: rgba($midnight, 0.04);
  color: $midnight;
}

.wall-card-tag-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.wall-card--dalston .wall-card-tag-dot { background: $aqua; }
.wall-card--putney .wall-card-tag-dot { background: darken($zest, 22%); }

.wall-card-text {
  margin: 0 0 14px;
  font-size: 0.9rem;
  line-height: 1.55;
  color: $charcoal;
  flex: 1;
}

.wall-card-text--clamped {
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.wall-more {
  align-self: flex-start;
  margin: -6px 0 14px;
  padding: 0;
  background: none;
  border: none;
  color: $aqua;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover,
  &:focus-visible {
    color: $midnight;
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid $aqua;
    outline-offset: 2px;
    border-radius: 3px;
  }
}

.wall-card-foot {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid rgba($midnight, 0.06);
  margin-top: auto;
}

.wall-card-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, $aqua 0%, $midnight 100%);
  color: #fff;
  font-weight: 700;
  font-size: 0.85rem;
  flex-shrink: 0;
}

.wall-card--putney .wall-card-avatar {
  background: linear-gradient(135deg, $zest 0%, $midnight 100%);
  color: $midnight;
}

.wall-card-meta {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
  flex: 1;
  min-width: 0;
}

.wall-card-author {
  font-weight: 700;
  color: $midnight;
  font-size: 0.88rem;
}

.wall-card-date {
  color: $carbon;
  font-size: 0.74rem;
  margin-top: 2px;
}

.wall-card-google {
  color: rgba($midnight, 0.3);
  font-size: 1rem;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  text-decoration: none;
  transition: color 0.2s ease, transform 0.2s ease;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: $midnight;
    transform: scale(1.1);
  }
  &:focus-visible {
    outline: 2px solid $aqua;
    outline-offset: 3px;
    border-radius: 3px;
  }
}

/* Reduce card width on smaller screens */

@media (max-width: 999px) {
  .wall-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .wall-card {
    height: 260px;
    padding: 18px;

    &--expanded {
      height: auto;
      min-height: 260px;
    }
  }

  .wall-body {
    padding: 0 18px;
  }

  .wall-carousel { gap: 6px; }

  .wall-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .stats-bar {
    padding: 0 18px;
    gap: 14px;
  }

  .stats-card {
    flex-direction: column;
    width: 100%;
  }

  .stats-card-location {
    border-right: none;
    border-bottom: 1px solid rgba($midnight, 0.08);
    padding: 18px 22px;
    min-width: 0;
    align-items: flex-start;
  }

  .stats-card::before {
    width: 100%;
    height: 4px;
  }

  .stats-card-platforms {
    justify-content: space-around;
  }

  .stat-col {
    min-width: 0;
    padding: 14px 10px;
    flex: 1;
  }

  .stat-col-rating {
    font-size: 1.25rem;
  }

  .wall-header {
    padding: 0 18px;
    margin-bottom: 22px;
  }
}

/* ================================================================
   SINGLE MODE (/dalston, /putney) — unchanged
================================================================ */

.reviews-section--single {
  padding: 0 5%;
  max-width: 1200px;
}

.reviews-header {
  margin-bottom: 32px;
  text-align: center;

  h2 {
    margin: 0 0 10px;
  }

  .reviews-intro {
    color: $carbon;
    font-size: 1rem;
    margin: 0 0 22px;
  }
}

.reviews-summary {
  display: inline-flex;
  align-items: center;
  gap: 20px;
  padding: 12px 20px;
  background: rgba($midnight, 0.04);
  border: 1px solid rgba($midnight, 0.08);
  border-radius: 999px;
  flex-wrap: wrap;
  justify-content: center;
}

.summary-stars {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: rgba($midnight, 0.28);
  font-size: 1rem;

  :deep(svg) {
    fill: currentColor;
    stroke: color-mix(in srgb, currentColor 55%, #000);
    stroke-width: 1.5;
  }

  .filled {
    color: $midnight;
  }

  .summary-score {
    margin-left: 8px;
    font-weight: 700;
    color: $midnight;
    font-size: 1.05rem;
    letter-spacing: 0.02em;
  }
}

.summary-meta {
  color: $carbon;
  font-size: 0.9rem;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: color 0.2s ease;

  i {
    font-size: 0.75rem;
    opacity: 0.6;
  }

  &:hover,
  &:focus-visible {
    color: $midnight;
    i { opacity: 1; }
  }
}

.platforms-strip {
  margin-top: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px 14px;

  .platforms-label {
    font-size: 0.8rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: $carbon;
    font-weight: 600;
  }
}

.platforms-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: inline-flex;
  flex-wrap: wrap;
  // Centre chips per row so when they wrap on narrow viewports
  // (Wellhub/Classpass each on their own line below ~600px) the
  // column reads centred rather than packing to the left edge.
  justify-content: center;
  gap: 10px;
}

.platform-chip {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  background: #fff;
  border: 1px solid rgba($midnight, 0.1);
  border-radius: 999px;
  box-shadow: 0 2px 6px rgba($midnight, 0.04);
  font-size: 0.88rem;
  text-decoration: none;
  color: inherit;
  transition: transform 0.2s ease, box-shadow 0.2s ease,
    border-color 0.2s ease, background 0.2s ease;

  &--link { cursor: pointer; }
  &--link:hover,
  &--link:focus-visible {
    transform: translateY(-1px);
    border-color: rgba($aqua, 0.5);
    box-shadow: 0 6px 16px -8px rgba($midnight, 0.2);
  }
  &--link:focus-visible {
    outline: 2px solid $aqua;
    outline-offset: 2px;
  }

  .platform-ext { font-size: 0.7rem; color: rgba($midnight, 0.5); }
  .platform-name { font-weight: 700; color: $midnight; }
  .platform-score {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: $midnight;
    font-weight: 600;

    i, :deep(svg) {
      color: $zest;
      fill: currentColor;
      stroke: color-mix(in srgb, currentColor 55%, #000);
      stroke-width: 1.5;
      filter: drop-shadow(0 1px 1px rgba($midnight, 0.15));
      font-size: 0.85rem;
    }
  }
  .platform-count { color: $carbon; font-size: 0.82rem; }
}

.reviews-body--single { position: relative; }

.reviews-carousel {
  position: relative;
  display: flex;
  align-items: stretch;
  gap: 12px;
}

.carousel-arrow {
  flex-shrink: 0;
  align-self: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid rgba($midnight, 0.12);
  background: #fff;
  color: $midnight;
  font-size: 1rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, background 0.2s ease,
    border-color 0.2s ease, box-shadow 0.2s ease, color 0.2s ease;
  box-shadow: 0 4px 12px -4px rgba($midnight, 0.2);

  &:hover:not(:disabled) {
    background: $zest;
    border-color: $zest;
    color: $midnight;
    transform: scale(1.05);
  }
  &:focus-visible {
    outline: 2px solid $aqua;
    outline-offset: 2px;
  }
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    box-shadow: none;
  }
}

.reviews-grid {
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-auto-rows: 1fr;
  gap: 18px;
}

.carousel-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 22px;
}

.carousel-dot {
  // WCAG 2.5.8 wants a 24×24 minimum touch target. The visible dot
  // stays 10×10 via the inner pseudo so the look is unchanged, but
  // the button itself is now a tappable 24×24 square.
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  position: relative;
  cursor: pointer;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 10px;
    height: 10px;
    margin: -5px 0 0 -5px;
    border-radius: 50%;
    background: rgba($midnight, 0.2);
    transition: background 0.2s ease, transform 0.2s ease;
  }

  &:hover::before { background: rgba($midnight, 0.4); }
  &:focus-visible {
    outline: 2px solid $aqua;
    outline-offset: 3px;
  }
  &--active::before {
    background: $midnight;
    transform: scale(1.2);
  }
}

// On mobile, suppress the dot strip entirely. With 30+ reviews per
// location the dots wrap to 3-4 messy rows and nobody clicks dot 27
// of 31 — prev/next arrows + their disabled boundary states cover
// navigation. Desktop keeps dots visible (where they look clean).
@media (max-width: 768px) {
  .carousel-dots { display: none; }
}

.review-card {
  // Fixed height matches the smoothness fix on the home-page wall.
  // Paging between carousel rows never shifts the viewport because
  // every card is the same height; `.review-text { flex: 1 }` absorbs
  // short reviews as whitespace and the footer stays pinned bottom.
  // Expansion via "Read more" is user-triggered — it gets the auto
  // escape hatch below so toggled cards can grow.
  height: 340px;
  background: #fff;
  border: 1px solid rgba($midnight, 0.08);
  border-radius: 14px;
  padding: 22px;
  display: flex;
  flex-direction: column;
  text-align: left;
  transition: transform 0.25s ease, box-shadow 0.25s ease,
    border-color 0.25s ease, height 0.25s ease;
  box-shadow: 0 2px 6px rgba($midnight, 0.04);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 32px -14px rgba($midnight, 0.22);
    border-color: rgba($aqua, 0.4);
  }

  &--expanded {
    height: auto;
    min-height: 340px;
  }
}

.review-stars {
  display: inline-flex;
  gap: 3px;
  color: rgba($midnight, 0.22);
  font-size: 0.95rem;
  margin-bottom: 14px;

  :deep(svg) {
    fill: currentColor;
    stroke: color-mix(in srgb, currentColor 55%, #000);
    stroke-width: 1.5;
  }

  .filled {
    color: $zest;
    filter: drop-shadow(0 1px 1px rgba($midnight, 0.15));
  }
}

.review-text {
  margin: 0 0 12px;
  padding: 0;
  font-size: 0.98rem;
  line-height: 1.6;
  color: $charcoal;
  quotes: none;
  flex: 1;

  &::before, &::after { content: none; }
}

.review-text--clamped {
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.review-more {
  align-self: flex-start;
  margin: 0 0 18px;
  padding: 0;
  background: none;
  border: none;
  // $aqua on white = ~2:1 (AA fail). darken 30% keeps the teal
  // accent and clears AA at ~5.4:1.
  color: darken($aqua, 30%);
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover, &:focus-visible {
    color: $midnight;
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid $aqua;
    outline-offset: 2px;
    border-radius: 3px;
  }
}

.review-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: auto;
  padding-top: 14px;
  border-top: 1px solid rgba($midnight, 0.06);
}

.review-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: linear-gradient(135deg, $aqua 0%, $midnight 100%);
  color: #fff;
  font-weight: 700;
  font-size: 1rem;
  flex-shrink: 0;
}

.review-meta-text {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
  flex: 1;
  min-width: 0;
}

.review-author {
  font-weight: 600;
  color: $midnight;
  font-size: 0.92rem;
}

.review-date {
  color: $carbon;
  font-size: 0.8rem;
  margin-top: 2px;
}

.review-google {
  color: rgba($midnight, 0.35);
  font-size: 1.1rem;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  text-decoration: none;
  transition: color 0.2s ease, transform 0.2s ease;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: $midnight;
    transform: scale(1.1);
  }
  &:focus-visible {
    outline: 2px solid $aqua;
    outline-offset: 3px;
    border-radius: 3px;
  }
}

@media (max-width: 999px) {
  .reviews-section--single .reviews-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .reviews-section--single {
    padding: 0 18px;
  }

  .reviews-summary {
    width: 100%;
    box-sizing: border-box;
    padding: 12px 16px;
    gap: 12px;
  }

  .reviews-carousel { gap: 6px; }

  .carousel-arrow {
    width: 38px;
    height: 38px;
    font-size: 0.85rem;
  }

  .reviews-section--single .reviews-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .review-card {
    padding: 20px;
    height: 300px;

    &--expanded {
      height: auto;
      min-height: 300px;
    }
  }
}
</style>
